// The client page's concierge payments: every payment request for this athlete,
// and the Request payment entry point (spec 2026-11-03-concierge-stripe-payments §3, §6).
// Renders nothing for an athlete who is not concierge (409) or not this partner's client (404).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  SCHEDULE_MISSING_TEXT, authConfig, dollars, errorDetail, progressText, statusLabel,
} from '../../lib/conciergeBilling';
import RequestPaymentDialog from './RequestPaymentDialog';

const STATUS_PILL = {
  sent: 'cn-pill--amber',
  active: 'cn-pill--green',
  past_due: 'cn-pill--red',
  complete: 'cn-pill--solid',
  cancelled: 'cn-pill--neutral',
};
const HIDDEN_ON = [404, 409];

function blockedReason(billing) {
  if (!billing.connected) return 'Connect Stripe on the Billing page first';
  if (billing.guardians.length === 0) return 'Add a guardian with an email first';
  if (billing.plans.length === 0) return 'No plans are set up yet — contact ProZpects to add them.';
  return '';
}

export default function PaymentPanel({ userId }) {
  const { API_URL } = useAuth();
  const [billing, setBilling] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [actionErr, setActionErr] = useState('');
  const [notice, setNotice] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // Bumped on every load and on every athlete switch: only the newest response
  // may land, so a slow reply for the previous athlete never overwrites this one.
  const seq = useRef(0);
  // Bumped only on an athlete switch: a Resend/Cancel reply for the previous athlete is dropped.
  const athleteGen = useRef(0);

  const load = useCallback(() => {
    const mine = ++seq.current;
    return axios.get(`${API_URL}/concierge-billing/athletes/${userId}/billing`, authConfig())
      .then(res => {
        if (mine !== seq.current) return;
        const d = res.data || {};
        setBilling({ connected: !!d.connected, plans: d.plans || [], guardians: d.guardians || [],
          requests: d.requests || [] });
        setHidden(false);
        setLoadErr('');
      })
      .catch(e => {
        if (mine !== seq.current) return;
        if (HIDDEN_ON.includes(e.response?.status)) { setHidden(true); return; }
        setLoadErr(errorDetail(e, 'Failed to load payments.'));
      });
  }, [API_URL, userId]);

  useEffect(() => {
    // A different athlete: drop everything shown for the previous one.
    setBilling(null);
    setHidden(false);
    setLoadErr('');
    setActionErr('');
    setNotice('');
    setDialogOpen(false);
    setConfirmingId(null);
    setBusyId(null);
    load();
    return () => { seq.current += 1; athleteGen.current += 1; };
  }, [load]);

  const act = (req, verb) => {
    setBusyId(req.id);
    setActionErr('');
    setNotice('');
    const gen = athleteGen.current;
    axios.post(`${API_URL}/concierge-billing/requests/${req.id}/${verb}`, {}, authConfig())
      .then(res => {
        if (gen !== athleteGen.current) return undefined;
        if (verb === 'resend') {
          setNotice(res.data?.email_sent === false
            ? 'The request is still open, but the email did not send. Try again shortly.'
            : 'Payment request resent.');
        }
        return load();
      })
      .catch(e => { if (gen === athleteGen.current) setActionErr(errorDetail(e, `Failed to ${verb} the request.`)); })
      .finally(() => { setBusyId(null); setConfirmingId(null); });
  };

  const onSent = (created) => {
    setDialogOpen(false);
    setActionErr('');
    setNotice(created?.email_sent === false
      ? 'Request created, but the email did not send. Use Resend.'
      : 'Payment request sent.');
    load();
  };

  if (hidden) return null;
  // Nothing visible while loading (most clients are not concierge), but a
  // marker, so a test can tell "still loading" from "hidden after a 404/409".
  if (!billing && !loadErr) return <div hidden data-testid="concierge-payment-loading" />;

  const reason = billing ? blockedReason(billing) : '';

  return (
    <section className="cn-card" data-testid="concierge-payment-panel">
      <div className="cn-card__head">
        <h2 className="cn-card__title">Payments</h2>
        {billing && (
          <button type="button" className="cn-dbtn" disabled={!!reason || dialogOpen}
                  aria-describedby={reason ? 'cn-pay-blocked' : undefined}
                  onClick={() => { setDialogOpen(true); setNotice(''); }}>
            Request payment
          </button>
        )}
      </div>
      {loadErr && <div className="cn-card__err">{loadErr}</div>}
      {reason && <div className="cn-row cn-field2__help" id="cn-pay-blocked">{reason}</div>}
      {actionErr && <div className="cn-card__err" role="alert">{actionErr}</div>}
      {notice && <div className="cn-row cn-field2__help" role="status">{notice}</div>}
      {dialogOpen && (
        <div className="cn-row">
          <RequestPaymentDialog userId={userId} plans={billing.plans} guardians={billing.guardians}
                                onSent={onSent} onCancel={() => setDialogOpen(false)} />
        </div>
      )}
      {billing && billing.requests.length === 0 && <div className="cn-empty-line">No payment requests yet.</div>}
      {billing && billing.requests.map(req => {
        const progress = progressText(req);
        const busy = busyId === req.id;
        return (
          <div key={req.id} className="cn-row cn-payreq" data-request-id={req.id}>
            <div>
              <div className="cn-camp__name">
                {req.plan_name || 'Plan'} · {req.pay_option === 'plan' ? 'Payment plan' : 'Pay in full'}
              </div>
              <div className="cn-camp__sub">
                {[`${dollars(req.amount_paid_cents || 0)} paid`, progress].filter(Boolean).join(' · ')}
              </div>
              {req.schedule_missing && (
                <div className="cn-camp__sub cn-empty-line--err">{SCHEDULE_MISSING_TEXT}</div>
              )}
              {req.status === 'sent' && (
                <div className="cn-dialog__actions">
                  {confirmingId === req.id ? (
                    <>
                      <button type="button" className="cn-todo__action cn-todo__action--danger" disabled={busy}
                              onClick={() => act(req, 'cancel')}>Yes, cancel request</button>
                      <button type="button" className="cn-todo__action" disabled={busy}
                              onClick={() => setConfirmingId(null)}>Keep it</button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="cn-todo__action" disabled={busy}
                              aria-label={`Resend ${req.plan_name || 'payment'} request`}
                              onClick={() => act(req, 'resend')}>Resend</button>
                      <button type="button" className="cn-todo__action cn-todo__action--danger" disabled={busy}
                              aria-label={`Cancel ${req.plan_name || 'payment'} request`}
                              onClick={() => setConfirmingId(req.id)}>Cancel</button>
                    </>
                  )}
                </div>
              )}
            </div>
            <span className={`cn-pill ${STATUS_PILL[req.status] || 'cn-pill--neutral'}`}>{statusLabel(req.status)}</span>
          </div>
        );
      })}
    </section>
  );
}
